import * as React from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { NameDialogUI } from '../NameDialogUI/NameDialogUI'
import type { LocalMates } from '../../../../deps/localMates/localMates/localMates.types'
import type { AuthDep } from '../../../../deps/auth/auth.types'
import type { VideoDep } from '../../../../deps/video/video/video.types'
import type { DeviceProfiles } from '../../../../deps/localMates/deviceProfiles/deviceProfiles.types'
import type { RoomDep } from '../../../../deps/room/room.types'
import type { ConnectedControllers } from '../../../../deps/input/connectedControllers/connectedControllers.types'
import { isPresetThumbnailValue, resolveThumbnailUrl } from '../../../../assets/avatarPresets/avatarPresets'

const MAX_THUMBNAIL_HISTORY = 10

interface NameDialogProps {
  localMates: LocalMates
  auth: AuthDep
  video: VideoDep
  videoEnabled: boolean
  isSharedScreen: boolean
  isOwner: boolean
  deviceProfiles: DeviceProfiles
  connectedControllers: ConnectedControllers
  getThumbnailHistory: RoomDep['actions']['getThumbnailHistory']
  deleteThumbnailFromHistory: RoomDep['actions']['deleteThumbnailFromHistory']
  updateRoom: RoomDep['connection']['updateRoom']
}

async function dataUrlToFile(dataUrl: string, filename: string): Promise<File> {
  const res = await fetch(dataUrl)
  const blob = await res.blob()
  return new File([blob], filename, { type: blob.type || 'image/png' })
}

/** Moves `url` to the front of `history` (inserting it if new), capped - mirrors the room's own MRU logic (packages/room matesInRoom.ts) for optimistic UI. */
function withThumbnailMostRecent(history: string[], url: string): string[] {
  return [url, ...history.filter((existing) => existing !== url)].slice(0, MAX_THUMBNAIL_HISTORY)
}

export function NameDialog({ localMates, auth, video, videoEnabled, isSharedScreen, isOwner, deviceProfiles, connectedControllers, getThumbnailHistory, deleteThumbnailFromHistory, updateRoom }: NameDialogProps) {
  useSignals()

  // In shared-screen mode there's no single "the" mate for this client - resolve
  // whichever mate belongs to the controller most recently used, so the header's
  // "Me" entry point (unlike clicking a specific mate tile) is unambiguous.
  const lastActiveControllerId = connectedControllers.lastActiveControllerId.value
  const mate = isSharedScreen
    ? localMates.localMates.value.find((m) => m.controllerId === lastActiveControllerId)
      ?? localMates.localMates.value[0]
    : localMates.localMates.value[0]
  const controllerLabel = isSharedScreen && mate?.controllerId ? `Controller ${mate.controllerId}` : undefined
  const isOpen = localMates.showNameDialog.value
  const [thumbnailHistory, setThumbnailHistory] = React.useState<string[]>([])

  React.useEffect(() => {
    if (!isOpen || !mate?.persistentId) {
      setThumbnailHistory([])
      return
    }
    let cancelled = false
    getThumbnailHistory(mate.persistentId)
      .then((history) => { if (!cancelled) setThumbnailHistory(history) })
      .catch((err) => console.error('Failed to load thumbnail history:', err))
    return () => { cancelled = true }
  }, [isOpen, mate?.persistentId, getThumbnailHistory])

  const syncProfileImageIfSignedIn = (file: File) => {
    if (!auth.currentUser.value) return
    auth.setProfileImage(file).catch((err) => console.error('Failed to update profile image:', err))
  }

  const devices = video.devices.value

  return (
    <NameDialogUI
      isOpen={isOpen}
      onClose={() => { localMates.showNameDialog.value = false }}
      videoEnabled={videoEnabled}
      name={mate?.name || ''}
      onNameChange={(name) => {
        if (mate) localMates.updateMateName(mate.mateId, name)
      }}
      thumbnailUrl={mate?.thumbnailUrl}
      thumbnailHistory={thumbnailHistory}
      onThumbnailUpload={(dataUrl, file) => {
        if (!mate) return
        localMates.updateMateThumbnail(mate.mateId, dataUrl)
        setThumbnailHistory((prev) => withThumbnailMostRecent(prev, dataUrl))
        // If signed in, also persist it as the account's Clerk profile photo so it
        // follows this user across rooms/devices - not just this room.
        syncProfileImageIfSignedIn(file)
      }}
      onThumbnailSelect={(url) => {
        if (!mate) return
        localMates.updateMateThumbnail(mate.mateId, url)
        // Presets already have a permanent home in the picker grid - only real
        // uploads/history entries belong in the "previously uploaded" MRU list.
        if (!isPresetThumbnailValue(url)) {
          setThumbnailHistory((prev) => withThumbnailMostRecent(prev, url))
        }
        // Resolve first so this works for both a real URL (data:/https:) and a
        // preset id like "preset:bear", which isn't itself fetchable.
        const resolvedUrl = resolveThumbnailUrl(url)
        if (resolvedUrl) {
          dataUrlToFile(resolvedUrl, 'profile.png').then(syncProfileImageIfSignedIn).catch((err) => {
            console.error('Failed to sync selected photo to profile:', err)
          })
        }
      }}
      onThumbnailDelete={(url) => {
        if (!mate?.persistentId) return
        setThumbnailHistory((prev) => prev.filter((existing) => existing !== url))
        deleteThumbnailFromHistory(mate.persistentId, url).catch((err) => {
          console.error('Failed to delete thumbnail:', err)
        })
      }}
      onThumbnailClear={() => {
        if (mate) localMates.updateMateThumbnail(mate.mateId, '')
      }}
      isSharedScreen={isSharedScreen}
      onSwitchProfile={
        isSharedScreen && mate?.controllerId
          ? () => {
              deviceProfiles.requestPicker(mate.controllerId!)
              localMates.showNameDialog.value = false
            }
          : undefined
      }
      controllerLabel={controllerLabel}
      isOwner={isOwner}
      onToggleVideoEnabled={(next) => updateRoom({ videoEnabled: next })}
      cameras={devices.cameras}
      mics={devices.mics}
      selectedCameraId={devices.selectedCameraId}
      selectedMicId={devices.selectedMicId}
      onSelectCamera={video.setCameraDevice}
      onSelectMic={video.setMicDevice}
    />
  )
}
